// Brand Controls redesign — Advanced Editing tab.
//
// Editable 5 voice attributes (inline textarea edit -> local mutate),
// removable avoid-list chips + add, and a real Generate-sample test panel
// (generateTestContent). Confidence rings render only where a real value
// exists in GeneratedAttributes (tone / sentence length / readability);
// perspective + emphasis have none, so they show no ring — nothing faked.

import { Save, Plus, X, Play, Edit2, AlertTriangle } from "lucide-react";
import type {
  VoiceProfile,
  GeneratedAttributes,
} from "../../../services/brand/brand-voice-service";
import { ConfidenceRing } from "./shared";

type FieldKey = "tone" | "sentenceStructure" | "vocabulary" | "perspective" | "emphasis";

export interface AdvancedViewProps {
  profile: VoiceProfile;
  attrs: GeneratedAttributes | null;
  editingField: FieldKey | null;
  editValue: string;
  onStartEdit: (field: FieldKey) => void;
  onEditChange: (v: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  isAddingTerm: boolean;
  toggleAddTerm: () => void;
  newTerm: string;
  onNewTerm: (v: string) => void;
  onAddTerm: () => void;
  onRemoveTerm: (term: string) => void;
  isTesting: boolean;
  testTitle: string;
  testContent: string;
  onGenerateTest: () => void;
  onSaveVersion: () => void;
  saved: boolean;
}

export function AdvancedView(props: AdvancedViewProps) {
  const {
    profile,
    attrs,
    editingField,
    editValue,
    onStartEdit,
    onEditChange,
    onSaveEdit,
    onCancelEdit,
    isAddingTerm,
    toggleAddTerm,
    newTerm,
    onNewTerm,
    onAddTerm,
    onRemoveTerm,
    isTesting,
    testTitle,
    testContent,
    onGenerateTest,
    onSaveVersion,
    saved,
  } = props;

  const fields: { key: FieldKey; label: string; conf?: number }[] = [
    { key: "tone", label: "Tone", conf: attrs?.tone?.confidence },
    { key: "sentenceStructure", label: "Sentence structure", conf: attrs?.sentenceLength?.confidence },
    { key: "vocabulary", label: "Vocabulary", conf: attrs?.readabilityLevel?.confidence },
    { key: "perspective", label: "Perspective" },
    { key: "emphasis", label: "Emphasis" },
  ];

  const avoidList = profile.avoidList || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--foreground)", margin: "0 0 3px" }}>Voice attributes</h2>
          <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", margin: 0 }}>
            Fine-tune each aspect. Every save creates a new version you can roll back to.
          </p>
        </div>
        <button
          onClick={onSaveVersion}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap", flexShrink: 0, fontSize: 13, fontWeight: 600, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 9, padding: "8px 14px", cursor: "pointer" }}
        >
          <Save style={{ width: 14, height: 14, color: "var(--muted-foreground)" }} strokeWidth={1.7} />
          {saved ? "Saved" : "Save as new version"}
        </button>
      </div>

      {/* attribute rows */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-sm)" }}>
        {fields.map((f, i) => {
          const editing = editingField === f.key;
          const low = typeof f.conf === "number" && f.conf < 60;
          const value = editing ? editValue : String((profile as any)[f.key] || "");
          return (
            <div key={f.key} style={{ borderBottom: i < fields.length - 1 ? "1px solid var(--border)" : "none", padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)" }}>{f.label}</span>
                    {typeof f.conf === "number" && <ConfidenceRing pct={f.conf} size={28} />}
                  </div>

                  {editing ? (
                    <div>
                      <textarea
                        className="bc-input"
                        value={editValue}
                        onChange={(e) => onEditChange(e.target.value)}
                        rows={3}
                        style={{ width: "100%", padding: "11px 13px", border: "1px solid var(--border)", borderRadius: 10, background: "var(--background)", fontSize: 14, fontFamily: "var(--font-sans)", color: "var(--foreground)", outline: "none", resize: "vertical" }}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
                        <button onClick={onSaveEdit} style={{ fontSize: 12.5, fontWeight: 600, color: "var(--on-teal-text)", background: "var(--alpha-teal)", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer" }}>Save</button>
                        <button onClick={onCancelEdit} style={{ fontSize: 12.5, fontWeight: 600, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 14px", cursor: "pointer" }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--foreground)", margin: 0 }}>{value}</p>
                      {low && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, color: "var(--warning-text)" }}>
                          <AlertTriangle style={{ width: 13, height: 13 }} strokeWidth={1.8} />
                          Least sure here. Add a sample in Quick Setup to sharpen it.
                        </div>
                      )}
                    </>
                  )}
                </div>

                {!editing && (
                  <button
                    onClick={() => onStartEdit(f.key)}
                    title={`Edit ${f.label.toLowerCase()}`}
                    style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, background: "var(--card)", border: "1px solid var(--border)", cursor: "pointer", color: "var(--muted-foreground)" }}
                  >
                    <Edit2 style={{ width: 15, height: 15 }} strokeWidth={1.7} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* avoid list */}
      <div style={{ border: "1px solid var(--border)", borderLeft: "3px solid var(--infrared-coral)", borderRadius: 14, padding: 20, boxShadow: "var(--elevation-sm)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)", margin: "0 0 3px" }}>Words &amp; phrases to avoid</h3>
            <p style={{ fontSize: 13, color: "var(--muted-foreground)", margin: 0 }}>AI actively steers around these in every generated asset.</p>
          </div>
          <button
            onClick={toggleAddTerm}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 13px", cursor: "pointer" }}
          >
            <Plus style={{ width: 14, height: 14 }} strokeWidth={2} />
            Add term
          </button>
        </div>

        {isAddingTerm && (
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <input
              className="bc-input"
              type="text"
              value={newTerm}
              onChange={(e) => onNewTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onAddTerm(); }}
              placeholder="e.g. hassle-free"
              style={{ flex: 1, minWidth: 200, height: 40, padding: "0 13px", border: "1px solid var(--border)", borderRadius: 9, background: "var(--background)", fontSize: 14, fontFamily: "var(--font-sans)", color: "var(--foreground)", outline: "none" }}
            />
            <button onClick={onAddTerm} style={{ fontSize: 13, fontWeight: 600, color: "var(--on-teal-text)", background: "var(--alpha-teal)", border: "none", borderRadius: 9, padding: "0 16px", height: 40, cursor: "pointer" }}>Add</button>
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {avoidList.length === 0 && (
            <span style={{ fontSize: 13, color: "var(--muted-foreground)" }}>No terms yet. Add words the AI should steer around.</span>
          )}
          {avoidList.map((t, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 9999, background: "var(--infrared-coral-10)", border: "1px solid var(--infrared-coral-20)" }}>
              <span style={{ fontSize: 13, color: "var(--foreground)" }}>{t}</span>
              <button onClick={() => onRemoveTerm(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted-foreground)", display: "inline-flex", padding: 0 }}>
                <X style={{ width: 13, height: 13 }} strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* test panel */}
      <div style={{ border: "1px solid var(--border)", borderRadius: 14, padding: 20, boxShadow: "var(--elevation-sm)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)", margin: "0 0 3px" }}>Hear it in your voice</h3>
            <p style={{ fontSize: 13, color: "var(--muted-foreground)", margin: 0 }}>Generate a sample so you can feel the profile before it shapes real assets.</p>
          </div>
          <button
            onClick={onGenerateTest}
            disabled={isTesting}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "#fff", background: "var(--orb-gradient)", border: "none", borderRadius: 9, padding: "8px 15px", cursor: isTesting ? "default" : "pointer", boxShadow: "var(--shadow-spectral-violet-glow)" }}
          >
            {isTesting ? (
              <span className="bc-spin" style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: 9999, display: "inline-block" }} />
            ) : (
              <Play style={{ width: 14, height: 14 }} strokeWidth={1.8} />
            )}
            {isTesting ? "Generating..." : "Generate sample"}
          </button>
        </div>
        {testContent && (
          <div className="bc-fade" style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, background: "var(--muted)" }}>
            {testTitle && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)", marginBottom: 6 }}>{testTitle}</div>}
            <p style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--foreground)", margin: 0, whiteSpace: "pre-wrap" }}>{testContent}</p>
          </div>
        )}
      </div>
    </div>
  );
}
